import {inject, observer} from 'mobx-react';
import qs from 'qs';
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {Container} from 'reactstrap';
import {MapContext} from '../../apiContexts';
import {dateToDate} from '../../helpers/date';
import {getJourneyReport, getRouteReport} from '../../services/reportService';
import JourneyList from '../../views/reports/journey-list';
import useTimezone from '../../hooks/use-timezone';

const flattenSections = (sections) =>
    sections.flatMap((section) =>
        section.items.map((item) => ({
            ...item,
            deviceName: section.deviceName,
            groupName: section.groupName,
        })),
    );

const JourneyReportContainer = (props) => {
    const {
        location,
    } = props;

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [allStops, setAllStops] = useState([]);
    const [selectingRoute, setSelectingRoute] = useState(false);
    const {devices, server} = props;
    const {distanceUnit, volumeUnit, speedUnit} = server.attributes;
    const {timezone} = useTimezone();
    const search = useMemo(() => location.search, [location.search]);
    const {setData, setSelectedItem, setSelectedRow} = useContext(MapContext);

    const activeRef = useRef();

    const getReport = useCallback(async (params) => {
        setLoading(true);
        try {
            const sections = await getJourneyReport(params);
            const data = flattenSections(sections);
            setItems(data);

            const stops = data.filter((item) => item.type === 'stop');
            setAllStops(stops);
            setData({type: 'journey_init', data: stops});
        } finally {
            setLoading(false);
        }
    }, []);

    const selectTrip = useCallback(
        async (trip) => {
            const {startTime, endTime, deviceId, id} = trip;
            const {from, to} = dateToDate(startTime, endTime);
            const requestIndex = activeRef.current;

            setSelectingRoute(true);
            try {
                const positions = await getRouteReport(
                    qs.stringify({deviceId, from, to}, {indices: false}),
                );

                // a newer row click superseded this one while the fetch was in flight
                if (activeRef.current !== requestIndex) return;

                setData({
                    type: 'journey_trip',
                    id,
                    deviceId,
                    data: positions,
                    stops: allStops,
                });

                setSelectedItem(trip);
                setSelectedRow({id: trip.id});
            } catch (err) {
                console.error('Failed to fetch journey trip route', err);
            } finally {
                setSelectingRoute(false);
            }
        },
        [allStops],
    );

    const selectStop = useCallback(
        (stop) => {
            setSelectedItem({
                ...stop,
                longitude: stop.startLon,
                latitude: stop.startLat,
            });
            setSelectedRow({id: stop.id});
        },
        [],
    );

    const onSelect = useCallback(
        ({index}) => {
            if (activeRef && activeRef.current === index) return;

            const item = items[index];
            if (!item) return;

            let itemActive = null;

            if (activeRef && activeRef.current >= 0) {
                itemActive = items[activeRef.current];
            }

            const newItems = items.concat();

            newItems[index] = {...item, isActive: true};
            itemActive &&
            (newItems[activeRef.current] = {...itemActive, isActive: false});

            activeRef.current = index;

            setItems(newItems);

            if (item.type === 'trip') {
                selectTrip(item);
            } else {
                selectStop(item);
            }
        },
        [items, selectTrip, selectStop],
    );

    useEffect(() => {
        if (search && devices.list.length && location.hash === '#journey') {
            const {markers, ...params} = qs.parse(search, {
                ignoreQueryPrefix: true,
                comma: true,
            });

            getReport(qs.stringify(params, {indices: false}));
        }
    }, [search, devices.list]);

    return (
        <Container fluid className="px-4 position-relative h-100">
            <JourneyList
                data={items}
                loading={loading}
                selectingRoute={selectingRoute}
                {...{timezone, distanceUnit, volumeUnit, speedUnit, onSelect}}
            />
        </Container>
    );
};

const mapping = ({store}) => ({
    auth: store.auth,
    session: store.session,
    map: store.map,
    server: store.server,
    devices: store.devices,
});

export default inject(mapping)(observer(JourneyReportContainer));
